import { useMutation, useQuery } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";

import { brand } from "../../config/brand";
import { api } from "../../lib/api";
import { PlatformIcon, useLinkPlatforms } from "../profile/links";
import { rooms, type Room } from "./api";

/**
 * Where the show is also happening, attached to the source switch.
 *
 * ## Why it moved out of the settings dialog
 *
 * It was a row of chips under "Also streaming on", behind a button, in a dialog
 * a host opens once a set - and it is the one thing a listener most needs to
 * know, because when it is set the room's chat is replaced by a link telling
 * them the conversation is elsewhere. A setting that changes what the audience
 * sees does not belong two clicks deep beside the queue rules.
 *
 * On the source switch it is next to the thing it qualifies. "Audio" and
 * "Audio: On Twitch" are two different broadcasts, and now they look it.
 *
 * ## A split button, not a fourth mode
 *
 * Twitch is not a *source* - the audio still comes from the catalogue. Making
 * it a fourth segment beside Audio, Studio and External would say it was, and
 * would leave a host unable to express "studio video, and also on Twitch". So
 * the platform hangs off the Audio segment as a menu of its own, with its own
 * click target and a rule between them.
 *
 * ## Absent rather than empty
 *
 * A host with no live-capable link on their profile gets no caret at all. The
 * dialog used to explain what to go and add; a disabled caret on a switch has
 * nowhere to say that, and a menu whose only entry is "no" is a worse answer
 * than no menu.
 */
export function SimulcastMenu({
  room,
  onChange,
  disabled = false,
}: {
  room: Room;
  onChange: () => void;
  disabled?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const wrap = useRef<HTMLSpanElement>(null);
  const { platforms } = useLinkPlatforms();

  /*
   * Their own links, from their public profile.
   *
   * `/me` is the obvious-looking endpoint and it is the wrong one: it returns
   * the account - identity, roles, permissions - and carries no profile links
   * at all. `/users/{username}` is what the settings page already reads, so it
   * is the same query key and the same normalised shape.
   */
  const { data: profile } = useQuery({
    queryKey: ["profile", room.username],
    queryFn: () =>
      api.get<{ links: { platform: string }[] }>(`/users/${room.username}`),
  });

  const update = useMutation({
    mutationFn: (platform: string | null) =>
      rooms.settings({ simulcast_platform: platform }),
    onSuccess: () => {
      setOpen(false);
      onChange();
    },
  });

  const eligible = platforms.filter(
    (p) =>
      p.live && (profile?.links ?? []).some((l) => l.platform === p.platform),
  );

  useEffect(() => {
    if (!open) return;

    const away = (event: MouseEvent) => {
      if (!wrap.current?.contains(event.target as Node)) setOpen(false);
    };

    const key = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false);
    };

    document.addEventListener("mousedown", away);
    document.addEventListener("keydown", key);

    return () => {
      document.removeEventListener("mousedown", away);
      document.removeEventListener("keydown", key);
    };
  }, [open]);

  // Nothing to choose between, so nothing to open. See the note above.
  if (eligible.length === 0) return null;

  return (
    <span ref={wrap} className="relative flex items-center">
      {/* The rule the caret hangs off, so the two targets read as one control
          with two jobs rather than as two buttons that happen to touch. */}
      <span className="h-4 w-px bg-current opacity-25" aria-hidden />

      <button
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label="Where else this is streaming"
        data-simulcast-menu
        disabled={disabled}
        onClick={() => setOpen((current) => !current)}
        className="flex h-full items-center rounded-r-[var(--radius-pill)] pl-1.5 pr-2 transition-opacity hover:opacity-80 disabled:opacity-60"
      >
        <svg viewBox="0 0 16 16" className="h-3 w-3" fill="none" stroke="currentColor" strokeWidth="1.8">
          <path d="M4 6.5 8 10.5l4-4" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>

      {open && (
        <div
          role="menu"
          data-simulcast-options
          /*
            Opens from the caret rightwards. Anchoring it to the right edge
            put it off the left of the screen: the Audio segment is the first
            thing in the switch, so there is nothing to its left to open into.
          */
          className="absolute left-0 top-full z-40 mt-2 min-w-[11rem] overflow-hidden rounded-[var(--radius-card)] border border-line bg-surface py-1 shadow-xl"
        >
          {/*
            The platform's own name for the "no" option, because that is what
            it means: the show is here and nowhere else. "Audio only" described
            the source, which is the thing this control is deliberately not
            about.
          */}
          <Option
            active={room.simulcast === null}
            attr="none"
            disabled={update.isPending}
            onClick={() => update.mutate(null)}
          >
            On {brand.name}
          </Option>

          {eligible.map((p) => (
            <Option
              key={p.platform}
              active={room.simulcast?.platform === p.platform}
              attr={p.platform}
              disabled={update.isPending}
              onClick={() => update.mutate(p.platform)}
            >
              <PlatformIcon platform={p.platform} className="h-3.5 w-3.5" />
              On {p.label}
            </Option>
          ))}
        </div>
      )}
    </span>
  );
}

function Option({
  active,
  attr,
  children,
  ...rest
}: {
  active: boolean;
  attr: string;
  children: React.ReactNode;
  disabled?: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      role="menuitemradio"
      aria-checked={active}
      data-simulcast={attr}
      className={`flex w-full items-center gap-2 px-3 py-1.5 text-left text-[13px] transition-colors disabled:opacity-60 ${
        active ? "bg-raised text-ink" : "text-soft hover:bg-raised hover:text-ink"
      }`}
      {...rest}
    >
      {children}
    </button>
  );
}

/**
 * What the Audio segment is called right now.
 *
 * Plain when the show is only here, and named when it is not - which is the
 * whole point of moving this out of the dialog. Exported so the switch can
 * label itself without knowing how simulcast works.
 */
export function sourceLabel(room: Room): string {
  return room.simulcast ? `Audio: On ${room.simulcast.label}` : "Audio";
}
